import AudiencePage, {
  type AudienceFaq,
  type AudienceItem,
  type AudienceStat,
} from "@/components/public/audience-page"
import type { SeoMeta } from "@/types"

const PAINS: AudienceItem[] = [
  {
    title: "Commercial listings sit invisible on portals",
    description:
      "CoStar, Rightmove Commercial and EG Propertylink reach decision-makers \u2014 but LinkedIn and Instagram are where those same decision-makers actually spend time between meetings. A commercial listing with no social presence is invisible.",
  },
  {
    title: "Brief copy is dense and unsellable",
    description:
      "\u201C15,000 sq ft Class E, PC 2026, EPC B\u201D reads fine in a brochure. Spoken over a 20-second video with lifestyle context, floorplan flyovers, and tenant-facing captions, it becomes a scroll-stopping pitch.",
  },
  {
    title: "Investment teasers need more than a PDF",
    description:
      "When marketing an investment lot to a qualified list, video that can be dropped into LinkedIn DMs and targeted Meta ads outperforms a dry 2-page teaser \u2014 especially for off-market deals.",
  },
  {
    title: "Retained-mandate marketing should look retained",
    description:
      "A retained mandate is a client relationship. The marketing should look like it. QuiteRight gives commercial agents consistent, branded video production across every instruction \u2014 without a marketing team.",
  },
]

const FEATURES: AudienceItem[] = [
  { title: "Office, retail, industrial and mixed-use", description: "Templates tuned for each commercial asset class. An industrial unit is framed differently from a Grade-A office \u2014 the engine knows." },
  { title: "Tenant-facing and investor-facing modes", description: "Toggle between tenant marketing (space, flexibility, location) and investor marketing (yield, tenant covenant, WAULT) \u2014 different scripts, same listing." },
  { title: "Floorplan and CGI support", description: "Upload commercial floorplans, fit-out CGIs and aerial site shots. QuiteRight composes them into a cohesive social-first brief video." },
  { title: "LinkedIn-ready 1:1 renders", description: "Every render ships a square 1:1 variant native to LinkedIn feed \u2014 where commercial decision-makers actually scroll." },
  { title: "Custom CTAs per lot", description: "Investment teasers CTA to \u201Crequest the full memorandum\u201D. Tenant marketing CTAs to \u201Carrange an inspection.\u201D Different deals, different asks." },
  { title: "Portfolio-level branding", description: "Agency logo, colours and contact details applied consistently across every commercial instruction \u2014 so every LinkedIn impression builds your brand." },
]

const STATS: AudienceStat[] = [
  { value: "60s", label: "per commercial instruction, from brief to render" },
  { value: "1:1", label: "+ 9:16 + 16:9 for LinkedIn, Reels and YouTube", tone: "accent" },
  { value: "\u00A3499", label: "monthly, fully managed, unlimited renders" },
]

const FAQ: AudienceFaq[] = [
  {
    q: "Does QuiteRight work for office and industrial listings, not just residential?",
    a: "Yes. Commercial templates are tuned for office, retail, industrial and mixed-use listings. Upload floorplans, fit-out CGIs or aerial shots and the pipeline produces commercial-appropriate scripts and pacing \u2014 not residential-flavoured ones.",
  },
  {
    q: "Can I produce investor-facing and tenant-facing videos from the same listing?",
    a: "Yes. Two renders from the same listing, two different scripts. Investor videos focus on yield, covenant, term and lot size; tenant videos focus on space, flexibility, location and fit-out. Both can be rendered in under two minutes.",
  },
  {
    q: "Does this work for off-market and confidential deals?",
    a: "Yes. Mark a listing as unpublished / private \u2014 the render will still happen, but it won't appear on your public feed or index. You can share the private video URL manually with qualified parties via LinkedIn DM or email.",
  },
  {
    q: "What about LinkedIn publishing specifically?",
    a: "QuiteRight renders a LinkedIn-native 1:1 variant for every listing. Direct LinkedIn Company Page publishing is on the roadmap; today most of our commercial customers schedule LinkedIn posts from Hootsuite or Buffer using the QuiteRight export.",
  },
  {
    q: "How does this fit with CoStar and EG Propertylink?",
    a: "CoStar and EG remain your primary trade portals \u2014 QuiteRight isn't trying to replace them. Social video ads are a complementary layer, reaching decision-makers and occupiers on LinkedIn, Instagram and email where portal impressions don't land.",
  },
]

export default function ForCommercialAgents({ meta }: { meta: SeoMeta }) {
  return (
    <AudiencePage
      meta={meta}
      breadcrumbName="For Commercial Agents"
      heroEyebrow="For commercial property agents"
      heroHeadline="Tenant briefs, investment teasers, LinkedIn feed \u2014 one pipeline, every instruction"
      heroSubhead="Commercial listings don't sell themselves on CoStar alone. QuiteRight gives commercial agents branded social video for every instruction \u2014 tuned for office, retail, industrial and mixed-use \u2014 with investor and tenant variants rendered from the same listing."
      heroBadgeSmall="Built for commercial"
      heroBadgeLarge="not residential"
      painsHeadline="Why commercial marketing needs its own video stack"
      pains={PAINS}
      featuresEyebrow="Built for commercial"
      featuresHeadline="Designed for commercial briefs, not residential listings"
      features={FEATURES}
      statsHeadline="Commercial video marketing, in numbers"
      stats={STATS}
      faq={FAQ}
      ctaHeadline="Every commercial instruction deserves a branded video"
      ctaBody="Tenant marketing, investment teasers, off-market sends \u2014 one pipeline, every asset class, \u00A3499 a month fully managed."
      relatedKey="for/commercial-agents"
    />
  )
}
